<template>
  <div class="new-release-commit-button">
    <ElButton
      type="primary"
      :disabled="disabled"
      :icon="commitButtonIcon"
      @click="decideWhichModalToOpen"
    >
      {{ commitButtonText }}
    </ElButton>

    <PublishRequestModal
      v-model="publishRequestModal"
      :note.sync="developerNote"
      :file.sync="devloperFile"
      :release="release"
      :loading="loading"
      :app="app"
      @confirm="commitRelease"
    />
  </div>
</template>

<script>
import { isRTL } from '@/utils/layout';
import packageName from '@/mixins/packageName';
import { mapActions, mapGetters } from 'vuex';

import PublishRulesModal from '../../modal/PublishRulesModal';
import PublishRequestModal from '@@/app/AppReleases/common/PublishRequestModal';

import {
  APP_DETAILS,
  COMMIT_RELEASE,
  RELEASES,
  GET_AGENT_PREFERENCE,
} from '@/store/types';
import { BUNDLE_STATES } from '@@/app/common/constants';
import ErrorsListModal from '@@/common/ErrorsListModal';

export default {
  name: 'NewReleaseCommitButton',
  components: {
    PublishRequestModal,
  },
  mixins: [packageName],
  props: {
    release: {
      type: Object,
      required: true,
    },
    changelogs: {
      type: Object,
      required: true,
    },
    stagedRolloutPercentage: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    releaseGroups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      developerNote: '',
      publishRequestModal: false,
      devloperFile: null,
    };
  },
  computed: {
    ...mapGetters({
      app: APP_DETAILS,
      releases: RELEASES,
      getAgentPreference: GET_AGENT_PREFERENCE,
    }),
    isInBundleSteps() {
      const { bundle } = this.release;
      const bundleExists = Boolean(bundle);
      const bundleIsDone = bundle?.state === BUNDLE_STATES.DONE;
      const bundleFacedErrors = bundle?.state === BUNDLE_STATES.ERROR;
      return bundleExists && !bundleIsDone && !bundleFacedErrors;
    },
    isLastReleaseWaiting() {
      const { publishState } = (this.releases && this.releases[0]) || {};
      return publishState === 'waiting';
    },
    commitButtonText() {
      if (this.isInBundleSteps) {
        return this.$t('APP.RELEASES.CONTINUE');
      }

      return this.$t('APP.RELEASES.COMMIT');
    },
    commitButtonIcon() {
      if (this.isInBundleSteps) {
        if (isRTL()) {
          return 'el-icon-arrow-right';
        }

        return 'el-icon-arrow-left';
      }

      return 'el-icon-check';
    },
  },
  methods: {
    ...mapActions({
      commitUncommittedRelease: COMMIT_RELEASE,
      fetchAppDetails: APP_DETAILS,
    }),
    decideWhichModalToOpen() {
      const shouldShowPublishRules = this.getAgentPreference(
        this.packageName,
        'show_publish_rules',
      );

      if (shouldShowPublishRules) {
        this.showPublishRulesModal();
      } else {
        this.showPublishRequestModal();
      }
    },
    showPublishRulesModal() {
      if (!this.app?.category?.id) return;

      this.$modal
        .open({
          title: this.$t('APP.RELEASES.PUBLISH_RULES'),
          component: PublishRulesModal,
          width: '35%',
          lockScroll: true,
          props: {
            categoryId: this.app.category.id,
            haveDontShowAgainOption: true,
          },
        })
        .then(confirmed => {
          if (confirmed) {
            this.showPublishRequestModal();
          }
        });
    },
    confirmDeactivatingWaitingRelease() {
      return this.$confirm(
        this.$t('APP.RELEASES.NEW_RELEASE_WARN'),
        this.$t('WARNING'),
        {
          confirmButtonText: this.$t('APP.RELEASES.DEACTIVATE_WAITING_RELEASE'),
          cancelButtonText: this.$t('CANCEL'),
          confirmButtonClass: 'new-release__delete-confirm-button',
          type: 'error',
        },
      );
    },
    showPublishRequestModal() {
      if (this.isLastReleaseWaiting) {
        return this.confirmDeactivatingWaitingRelease()
          .then(() => {
            this.publishRequestModal = true;
          })
          .catch(() => {});
      }
      this.publishRequestModal = true;
    },
    commitRelease() {
      this.$emit('update:loading', true);

      const requestData = {
        packageName: this.packageName,
        changelogs: this.changelogs,
        releaseId: this.release.token,
        developerNote: this.developerNote,
        stagedRolloutPercentage: this.stagedRolloutPercentage,
        file: this.devloperFile,
      };

      if (this.releaseGroups.length > 0) {
        requestData['releaseGroups'] = this.releaseGroups[0];
      }

      return this.commitUncommittedRelease(requestData)
        .then(({ warnings }) => {
          if (warnings?.length) {
            const warningsText = warnings.join('<br>');

            this.$alert(warningsText, this.$t('WARNING'), {
              type: 'warning',
              confirmButtonText: this.$t('GOT_IT'),
              showCancelButton: false,
              dangerouslyUseHTMLString: true,
            });
          }

          this.fetchAppDetails(this.packageName);
        })
        .catch(({ message, errors = [] }) => {
          if (errors?.length) {
            this.$modal.open({
              component: ErrorsListModal,
              title: this.$t('ERROR'),
              props: { message, errors },
              width: '30%',
            });
          } else {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.$emit('update:loading', false);
        });
    },
  },
};
</script>

<style lang="scss">
.new-release-commit-button {
  margin-right: calc($global-margin / 2);
  margin-left: 0;

  @include rtl() {
    margin-right: 0;
    margin-left: calc($global-margin / 2);
  }
}
</style>
